import { Icon, IconName, TextInput } from '@mgmresorts/mgm-ui';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useContent } from '../../../hooks/useContent';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import { SignUpFormContext } from '../SignUpFormContext';

import {
  StyledPasswordHint,
  StyledHintContainer,
  StyledContainer,
  StyledPasswordHintList,
  StyledPasswordHintItem,
  StyledPasswordHintText,
} from './styles';

export function PasswordInput() {
  const trackEvent = useEvent();
  const { password, isLoading, bindPassword, invalidFields, passwordValidations, isValidPassword } =
    useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const [pwError, setPWError] = useState('');

  useEffect(() => {
    if (invalidFields.includes('password')) {
      const message = getContentByPath('accountCreation.fields.password.empty') as string;
      if (pwError !== message) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          {
            errorField: getContentByPath('accountCreation.fields.password.title'),
            errorDisplayed: message,
          },
        );
        setPWError(message);
      }
    } else if (password && !isValidPassword) {
      const message = getContentByPath('accountCreation.fields.password.policy') as string;
      if (pwError !== message) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          {
            errorField: getContentByPath('accountCreation.fields.password.title'),
            errorDisplayed: message,
          },
        );
        setPWError(message);
      }
    } else {
      setPWError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentByPath, invalidFields, trackEvent]);

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes('password')) {
      return getContentByPath('accountCreation.fields.password.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('accountCreation.fields.password.policy');
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath]);

  const error = useMemo(
    () => (invalidFields.includes('password') || (password && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, password],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      const className = !password ? '' : isValid ? 'hint-success' : 'hint-error';
      const iconName: IconName = !password || isValid ? 'checkmark-circle' : 'symbol-x-circle';

      const requirementText = getContentByPath(
        !password
          ? 'shared.fields.requirement'
          : isValid
          ? 'shared.fields.requirementMet'
          : 'shared.fields.requirementUnmet',
      );
      if (!password) {
        return (
          <StyledPasswordHintItem>
            <StyledPasswordHintText className={className} variant='body-regular-s' as='p'>
              {hint}
            </StyledPasswordHintText>
          </StyledPasswordHintItem>
        );
      }
      return (
        <StyledPasswordHint className={className} variant='body-regular-s' as='p'>
          <Icon
            name={iconName}
            size='small'
            role='img'
            aria-hidden={false}
            focusable
            aria-label={requirementText as string}
          />
          {hint}
        </StyledPasswordHint>
      );
    },
    [getContentByPath, passwordValidations, password],
  );
  const renderPasswordHints = () => (
    <>
      {passwordValidationHint('length', getContentByPath('changePassword.fields.newPassword.validation.lengthHint'))}
      {passwordValidationHint('number', getContentByPath('changePassword.fields.newPassword.validation.numberHint'))}
      {passwordValidationHint(
        'uppercase',
        getContentByPath('changePassword.fields.newPassword.validation.uppercaseHint'),
      )}
      {passwordValidationHint(
        'lowercase',
        getContentByPath('changePassword.fields.newPassword.validation.lowercaseHint'),
      )}
      {passwordValidationHint(
        'specialCharacter',
        getContentByPath('changePassword.fields.newPassword.validation.specialCharacterHint'),
      )}
    </>
  );
  return (
    <StyledContainer>
      <TextInput
        variant='password'
        testId='password'
        label={getContentByPath('accountCreation.fields.password.title')}
        id='password'
        error={!!error}
        hint={error as string}
        {...bindPassword}
        disabled={isLoading}
        maxLength={MaxLengths.Password}
        autoComplete='new-password'
      />
      <StyledHintContainer>
        <>
          {(!password || error) && (
            <>
              {!password ? (
                <StyledPasswordHintList>{renderPasswordHints()}</StyledPasswordHintList>
              ) : (
                <>{renderPasswordHints()}</>
              )}
            </>
          )}
          {password && !error && (
            <StyledPasswordHint variant='body-regular-s' className='hint-success'>
              <Icon
                name='checkmark-circle'
                size='small'
                role='img'
                aria-hidden={false}
                focusable
                aria-label={getContentByPath('accountCreation.fields.password.allMet')}
              />
              {getContentByPath('accountCreation.fields.password.allMet')}
            </StyledPasswordHint>
          )}
        </>
      </StyledHintContainer>
    </StyledContainer>
  );
}

PasswordInput.displayName = 'PasswordInputField';
